import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, X, Pencil, Loader2, Tag, Gauge } from "lucide-react";

const FREQUENCIES = ["daily", "weekly", "monthly", "quarterly", "yearly"];
const MEASUREMENT_TYPES = ["count", "currency", "percentage", "score", "boolean"];

export default function Settings() {
  const [tab, setTab] = useState("categories");
  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Configuration</div>
        <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
          Custom Settings
        </h2>
        <p className="text-slate-400 text-sm mt-2">
          Manage tie-up categories and KPI registry. Changes apply to new entries only — historical records keep their original labels.
        </p>
      </div>

      <div className="flex bg-slate-800/60 border border-slate-700 rounded-lg w-fit overflow-hidden">
        <button
          data-testid="settings-tab-categories"
          onClick={() => setTab("categories")}
          className={`px-4 py-2 text-sm flex items-center gap-2 ${tab === "categories" ? "bg-[#C9A24B] text-[#0F172A]" : "text-slate-300"}`}
        >
          <Tag size={14} /> Tie-Up Categories
        </button>
        <button
          data-testid="settings-tab-kpis"
          onClick={() => setTab("kpis")}
          className={`px-4 py-2 text-sm flex items-center gap-2 ${tab === "kpis" ? "bg-[#C9A24B] text-[#0F172A]" : "text-slate-300"}`}
        >
          <Gauge size={14} /> KPI Registry
        </button>
      </div>

      {tab === "categories" ? <CategoriesPanel /> : <KpisPanel />}
    </div>
  );
}

function CategoriesPanel() {
  const [list, setList] = useState([]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = () => api.get("/settings/categories").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const nm = newName.trim();
    if (!nm) { toast.error("Enter a category name"); return; }
    setSaving(true);
    try {
      await api.post("/settings/categories", { name: nm });
      toast.success("Category added");
      setNewName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/settings/categories/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const startEdit = (c) => { setEditId(c.id); setEditName(c.name); };
  const saveEdit = async (c) => {
    const nm = editName.trim();
    if (!nm) { toast.error("Name required"); return; }
    try {
      await api.patch(`/settings/categories/${c.id}`, { name: nm });
      toast.success("Renamed. Historical records keep their original label.");
      setEditId(null); setEditName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl p-4" data-testid="categories-add">
        <div className="text-sm text-slate-300 mb-2">Add new category</div>
        <div className="flex gap-2">
          <input
            data-testid="category-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. Fitness Trainer"
            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#C9A24B]"
          />
          <button
            data-testid="category-add-btn"
            onClick={add}
            disabled={saving}
            className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 text-sm flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
          </button>
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden" data-testid="categories-list">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800/50 text-slate-300 text-[11px] uppercase tracking-wider">
              <th className="text-left px-3 py-3 font-semibold">Name</th>
              <th className="text-left px-3 py-3 font-semibold">Status</th>
              <th className="text-left px-3 py-3 font-semibold w-56">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-slate-800" data-testid={`category-row-${c.id}`}>
                <td className="px-3 py-2.5 text-white font-medium">
                  {editId === c.id ? (
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white outline-none"
                      autoFocus
                    />
                  ) : c.name}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${c.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-600/30 text-slate-400"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1.5">
                    {editId === c.id ? (
                      <>
                        <button onClick={() => saveEdit(c)} className="text-[11px] bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded px-2 py-1 flex items-center gap-1">
                          <Check size={12} /> Save
                        </button>
                        <button onClick={() => { setEditId(null); setEditName(""); }} className="text-[11px] bg-slate-700 text-slate-300 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1">
                          <X size={12} /> Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button data-testid={`category-edit-${c.id}`} onClick={() => startEdit(c)} className="text-[11px] bg-slate-700 text-slate-200 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1">
                          <Pencil size={12} /> Rename
                        </button>
                        <button data-testid={`category-toggle-${c.id}`} onClick={() => toggleActive(c)} className={`text-[11px] rounded px-2 py-1 flex items-center gap-1 ${c.active ? "bg-slate-700 text-slate-200 hover:bg-slate-600" : "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"}`}>
                          {c.active ? "Deactivate" : "Activate"}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={3} className="text-center text-slate-500 py-6">No categories yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpisPanel() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", target: 0, frequency: "monthly", measurement_type: "count" });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const load = () => api.get("/settings/kpis").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) { toast.error("KPI name required"); return; }
    setSaving(true);
    try {
      await api.post("/settings/kpis", { ...form, name: form.name.trim(), target: Number(form.target) || 0 });
      toast.success("KPI added");
      setForm({ name: "", target: 0, frequency: "monthly", measurement_type: "count" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (k) => {
    try {
      await api.patch(`/settings/kpis/${k.id}`, { active: !k.active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const startEdit = (k) => { setEditId(k.id); setEditDraft({ name: k.name, target: k.target, frequency: k.frequency, measurement_type: k.measurement_type }); };
  const saveEdit = async (k) => {
    try {
      await api.patch(`/settings/kpis/${k.id}`, { ...editDraft, name: editDraft.name.trim(), target: Number(editDraft.target) || 0 });
      toast.success("KPI updated. Historical scorecard calculations are unaffected.");
      setEditId(null); setEditDraft({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl p-4" data-testid="kpis-add">
        <div className="text-sm text-slate-300 mb-2">Add new KPI</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input
            data-testid="kpi-new-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="KPI name"
            className="md:col-span-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none"
          />
          <input
            data-testid="kpi-new-target"
            type="number"
            value={form.target}
            onChange={(e) => setForm({ ...form, target: e.target.value })}
            placeholder="Target"
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none"
          />
          <select
            data-testid="kpi-new-frequency"
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value })}
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none"
          >
            {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select
            data-testid="kpi-new-measurement"
            value={form.measurement_type}
            onChange={(e) => setForm({ ...form, measurement_type: e.target.value })}
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none"
          >
            {MEASUREMENT_TYPES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="flex justify-end mt-2">
          <button
            data-testid="kpi-add-btn"
            onClick={add}
            disabled={saving}
            className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add KPI
          </button>
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden" data-testid="kpis-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/50 text-slate-300 text-[11px] uppercase tracking-wider">
                <th className="text-left px-3 py-3 font-semibold">Name</th>
                <th className="text-left px-3 py-3 font-semibold">Target</th>
                <th className="text-left px-3 py-3 font-semibold">Frequency</th>
                <th className="text-left px-3 py-3 font-semibold">Type</th>
                <th className="text-left px-3 py-3 font-semibold">Status</th>
                <th className="text-left px-3 py-3 font-semibold w-48">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((k) => {
                const isEdit = editId === k.id;
                return (
                  <tr key={k.id} className="border-t border-slate-800" data-testid={`kpi-row-${k.id}`}>
                    <td className="px-3 py-2.5 text-white font-medium">
                      {isEdit ? (
                        <input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white outline-none" />
                      ) : k.name}
                    </td>
                    <td className="px-3 py-2.5">
                      {isEdit ? (
                        <input type="number" value={editDraft.target} onChange={(e) => setEditDraft({ ...editDraft, target: e.target.value })}
                          className="w-24 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white outline-none" />
                      ) : k.target}
                    </td>
                    <td className="px-3 py-2.5 text-slate-300">
                      {isEdit ? (
                        <select value={editDraft.frequency} onChange={(e) => setEditDraft({ ...editDraft, frequency: e.target.value })}
                          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white outline-none">
                          {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                      ) : k.frequency}
                    </td>
                    <td className="px-3 py-2.5 text-slate-300">
                      {isEdit ? (
                        <select value={editDraft.measurement_type} onChange={(e) => setEditDraft({ ...editDraft, measurement_type: e.target.value })}
                          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white outline-none">
                          {MEASUREMENT_TYPES.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      ) : k.measurement_type}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${k.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-600/30 text-slate-400"}`}>
                        {k.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1.5">
                        {isEdit ? (
                          <>
                            <button onClick={() => saveEdit(k)} className="text-[11px] bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded px-2 py-1 flex items-center gap-1">
                              <Check size={12} /> Save
                            </button>
                            <button onClick={() => { setEditId(null); setEditDraft({}); }} className="text-[11px] bg-slate-700 text-slate-300 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1">
                              <X size={12} /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button data-testid={`kpi-edit-${k.id}`} onClick={() => startEdit(k)} className="text-[11px] bg-slate-700 text-slate-200 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1">
                              <Pencil size={12} /> Edit
                            </button>
                            <button data-testid={`kpi-toggle-${k.id}`} onClick={() => toggleActive(k)} className={`text-[11px] rounded px-2 py-1 flex items-center gap-1 ${k.active ? "bg-slate-700 text-slate-200 hover:bg-slate-600" : "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"}`}>
                              {k.active ? "Deactivate" : "Activate"}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-500 py-6">No KPIs configured.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
